import { Col, Row } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ClientNavigation } from '../../components/client/client-navigation';
import { Agent, Lead } from '../../types';
import { ClientConfig } from '../../types/config';
import { UserContext, UserType } from '../../UserContext';
import axios from 'axios';
import CustomAlert from '../../CustomAlert';
import { sanitizeHTML } from '../../utils';
import { isHTML } from '../agentSms/utils';
import {
  List as AntList,
  Tag as AntTag,
  Button,
  Card,
  Descriptions,
  Input,
  message,
  Spin,
  Tabs,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  TagsOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const AgentLeads = ({ apiUrl }: { apiUrl: string }): JSX.Element => {
  const { clientId, agentId } = useParams<{
    clientId: string;
    agentId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [filteredAgents, setFilteredAgents] = useState<Agent[]>([]);
  const [activeAgent, setActiveAgent] = useState<Agent | undefined>(undefined);
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [selectedLead, setSelectedLead] = useState<Lead | undefined>(undefined);
  const user = useContext<UserType>(UserContext);
  const [sidebarPage, setSidebarPage] = useState(1);
  const [sidebarHasMore, setSidebarHasMore] = useState(true);
  const loadingRef = useRef(false);
  const pageRef = useRef(1);
  const sidebarPageRef = useRef(1);

  // State for notes and tasks
  const [leadNotes, setLeadNotes] = useState<any[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [isCreateNoteVisible, setIsCreateNoteVisible] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [noteHeader, setNoteHeader] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [leadTasks, setLeadTasks] = useState<any[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [isCreateTaskVisible, setIsCreateTaskVisible] = useState(false);
  const [taskType, setTaskType] = useState('Follow Up');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskDetails, setTaskDetails] = useState('');

  // Additional state for bio, activity, tags
  const [leadBio, setLeadBio] = useState<any>(null);
  const [bioLoading, setBioLoading] = useState(false);
  const [leadActivity, setLeadActivity] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [leadTags, setLeadTags] = useState<string[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [tagAdding, setTagAdding] = useState(false);

  // Add helper function to check for HTML

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/configs/${clientId}`, {
          headers: user?.accessToken
            ? { Authorization: `Bearer ${user.accessToken}` }
            : {},
        });
        setClientConfig(response.data);
        const validAgents = (response.data.agents as Agent[]).filter(
          a => a.first_name != null,
        );
        setAgents(validAgents);
      } catch (err) {
        setError('Failed to fetch client config');
      }
    };
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, clientId, user?.accessToken]);

  useEffect(() => {
    setFilteredAgents(
      agentSearch === ''
        ? agents
        : agents.filter(agent => {
            const fullName =
              `${agent.first_name} ${agent.last_name}`.toLowerCase();
            const reversedFullName =
              `${agent.last_name} ${agent.first_name}`.toLowerCase();
            return (
              fullName.includes(agentSearch.toLowerCase()) ||
              reversedFullName.includes(agentSearch.toLowerCase())
            );
          }),
    );
  }, [agentSearch, agents]);

  useEffect(() => {
    if (!agentId && agents.length > 0) {
      setActiveAgent(agents[0]);
      setSearchParams({ agentId: agents[0].id });
    } else if (agentId && agents.length > 0) {
      const found = agents.find(a => a.id === agentId);
      if (found) {
        setActiveAgent(found);
      }
    }
  }, [agents, agentId, setSearchParams]);

  useEffect(() => {
    if (!activeAgent) {
      return;
    }
    const fetchLeads = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const params = new URLSearchParams({
          page: currentPage.toString(),
          pageSize: pageSize.toString(),
        });
        if (searchTerm.trim()) {
          params.append('search', searchTerm.trim());
        }
        const response = await axios.get(
          `${apiUrl}/api/agent/leads/${activeAgent.id}?${params.toString()}`,
        );
        setLeads(response.data.leads || []);
        setTotal(response.data.total || 0);
        setCurrentPage(response.data.page || 1);
        setPageSize(response.data.pageSize || pageSize);
      } catch (err: any) {
        setError(err?.message || 'Failed to fetch leads');
        setLeads([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAgent, currentPage, pageSize, searchTerm, apiUrl]);

  useEffect(() => {
    if (leads.length > 0 && !selectedLead) {
      setSelectedLead(leads[0]);
    }
  }, [leads, selectedLead]);

  const [leadSearch, setLeadSearch] = useState('');
  const filteredLeads =
    leadSearch === ''
      ? leads
      : leads.filter(lead => {
          const name = `${lead.first_name} ${lead.last_name}`.toLowerCase();
          const email = (lead.email || '').toLowerCase();
          const phone = (lead.phone || '').toLowerCase();
          const search = leadSearch.toLowerCase();
          return (
            name.includes(search) ||
            email.includes(search) ||
            phone.includes(search)
          );
        });

  useEffect(() => {
    const intervalId = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(intervalId);
  }, []);

  const fetchSidebarLeads = async (page: number, append: boolean) => {
    if (!activeAgent) {
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      if (leadSearch.trim()) {
        params.append('search', leadSearch.trim());
      }
      const response = await axios.get(
        `${apiUrl}/api/agent/leads/${activeAgent.id}?${params.toString()}`,
      );
      const newLeads = response.data.leads || [];
      const totalLeads = response.data.total || 0;
      console.log('fetchSidebarLeads', { page, append, newLeads });
      setTotal(totalLeads);
      setSidebarHasMore(page * pageSize < totalLeads);
      if (append) {
        setLeads(prev => {
          const existingIds = new Set(prev.map((l: Lead) => l.id));
          const uniqueNewLeads = newLeads.filter(
            (l: Lead) => !existingIds.has(l.id),
          );
          return [...prev, ...uniqueNewLeads];
        });
        sidebarPageRef.current = page;
      } else {
        setLeads(newLeads);
        sidebarPageRef.current = 1;
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to fetch leads');
      if (!append) {
        setLeads([]);
      }
      setSidebarHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadFirstThreePages = async () => {
      if (!activeAgent) {
        return;
      }
      setSidebarPage(1);
      setSidebarHasMore(true);
      setLeads([]);
      sidebarPageRef.current = 1;
      await fetchSidebarLeads(1, false);
      await fetchSidebarLeads(2, true);
      await fetchSidebarLeads(3, true);
      await fetchSidebarLeads(4, true);
    };
    loadFirstThreePages();
    // eslint-disable-next-line react-hooks/excessive-deps
  }, [activeAgent, leadSearch]);

  // Infinite scroll handler for sidebar
  const handleSidebarScroll = (e: React.UIEvent<HTMLElement, UIEvent>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop - clientHeight < 40 &&
      sidebarHasMore &&
      !loading &&
      leads.length < total
    ) {
      const nextPage = sidebarPageRef.current + 1;
      console.log('Scroll handler: loading next page', nextPage);
      fetchSidebarLeads(nextPage, true);
    }
  };

  // Debug: log leads before rendering
  console.log('Sidebar leads:', leads);

  // Fetch notes when selectedLead or activeAgent changes
  useEffect(() => {
    if (!selectedLead || !activeAgent) {
      return;
    }
    setNotesLoading(true);
    fetch(
      `${apiUrl}/api/agent/${activeAgent.id}/lead/${selectedLead.source_id}/notes`,
      {
        headers: {
          Authorization: user?.accessToken ? `Bearer ${user.accessToken}` : '',
        },
      },
    )
      .then(res => res.json())
      .then(data => setLeadNotes(Array.isArray(data) ? data : []))
      .catch(() => setLeadNotes([]))
      .finally(() => setNotesLoading(false));
  }, [selectedLead, activeAgent, apiUrl, user?.accessToken]);

  // Fetch tasks when selectedLead or activeAgent changes
  useEffect(() => {
    if (!selectedLead || !activeAgent) {
      return;
    }
    setTasksLoading(true);
    fetch(`${apiUrl}/api/agents/${activeAgent.id}/tasks`, {
      headers: {
        Authorization: user?.accessToken ? `Bearer ${user.accessToken}` : '',
      },
    })
      .then(res => res.json())
      .then(data => {
        // Filter tasks for this lead
        setLeadTasks(
          Array.isArray(data)
            ? data.filter((t: any) => t.lead_id === selectedLead.source_id)
            : [],
        );
      })
      .catch(() => setLeadTasks([]))
      .finally(() => setTasksLoading(false));
  }, [selectedLead, activeAgent, apiUrl, user?.accessToken]);

  // Create note handler
  const handleCreateNote = async () => {
    if (!noteBody.trim() || !selectedLead || !activeAgent) {
      message.error('Note body is required');
      return;
    }
    setNoteLoading(true);
    try {
      await fetch(
        `${apiUrl}/api/agent/${activeAgent.id}/lead/${selectedLead.source_id}/notes`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: user?.accessToken
              ? `Bearer ${user.accessToken}`
              : '',
          },
          body: JSON.stringify({ body: noteBody, header: noteHeader }),
        },
      );
      message.success('Note created');
      setIsCreateNoteVisible(false);
      setNoteBody('');
      setNoteHeader('');
      // Refresh notes
      setNotesLoading(true);
      const res = await fetch(
        `${apiUrl}/api/agent/${activeAgent.id}/lead/${selectedLead.source_id}/notes`,
        {
          headers: {
            Authorization: user?.accessToken
              ? `Bearer ${user.accessToken}`
              : '',
          },
        },
      );
      const notes = await res.json();
      setLeadNotes(Array.isArray(notes) ? notes : []);
    } catch {
      message.error('Failed to create note');
    } finally {
      setNoteLoading(false);
      setNotesLoading(false);
    }
  };

  // Create task handler (minimal, for demo)
  const handleCreateTask = async () => {
    if (!taskDetails.trim() || !selectedLead || !activeAgent) {
      message.error('Task details are required');
      return;
    }
    setTasksLoading(true);
    try {
      await fetch(`${apiUrl}/api/tasks/${activeAgent.id}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: user?.accessToken ? `Bearer ${user.accessToken}` : '',
        },
        body: JSON.stringify({
          lead_id: selectedLead.source_id,
          task_type: taskType,
          due_date: taskDueDate,
          details: taskDetails,
        }),
      });
      message.success('Task created');
      setIsCreateTaskVisible(false);
      setTaskDetails('');
      setTaskDueDate('');
      // Refresh tasks
      setTasksLoading(true);
      const res = await fetch(`${apiUrl}/api/agents/${activeAgent.id}/tasks`, {
        headers: {
          Authorization: user?.accessToken ? `Bearer ${user.accessToken}` : '',
        },
      });
      const tasks = await res.json();
      setLeadTasks(
        Array.isArray(tasks)
          ? tasks.filter((t: any) => t.lead_id === selectedLead.source_id)
          : [],
      );
    } catch {
      message.error('Failed to create task');
    } finally {
      setTasksLoading(false);
    }
  };

  // Fetch bio/profile info when selectedLead changes
  useEffect(() => {
    if (!selectedLead || !clientId) {
      return;
    }
    setBioLoading(true);
    fetch(`${apiUrl}/api/Lead/${clientId}/${selectedLead.id}`)
      .then(res => res.json())
      .then(data => setLeadBio(data))
      .catch(() => setLeadBio(null))
      .finally(() => setBioLoading(false));
  }, [selectedLead, clientId, apiUrl]);

  // Fetch activity (last communication, etc.)
  useEffect(() => {
    if (!selectedLead || !clientId || !activeAgent) {
      return;
    }
    setActivityLoading(true);
    fetch(`${apiUrl}/api/leads2/${clientId}/${activeAgent.id}`)
      .then(res => res.json())
      .then(data => {
        // Find this lead's activity
        const found = Array.isArray(data)
          ? data.find((l: any) => l.id === selectedLead.id)
          : null;
        setLeadActivity(
          found?.activity_summary ? [found.activity_summary] : [],
        );
      })
      .catch(() => setLeadActivity([]))
      .finally(() => setActivityLoading(false));
  }, [selectedLead, clientId, activeAgent, apiUrl]);

  // Fetch tags
  useEffect(() => {
    if (!selectedLead) {
      return;
    }
    setTagsLoading(true);
    // Assume tags are in leadBio or fetch from a tags endpoint if available
    if (leadBio && leadBio.tags) {
      setLeadTags(
        Array.isArray(leadBio.tags)
          ? leadBio.tags.map((t: any) => t.name || t)
          : [],
      );
      setTagsLoading(false);
    } else {
      setLeadTags([]);
      setTagsLoading(false);
    }
  }, [selectedLead, leadBio]);

  // Add tag handler
  const handleAddTag = async () => {
    if (!newTag.trim() || !selectedLead) {
      return;
    }
    setTagAdding(true);
    try {
      await fetch(`${apiUrl}/api/leads/${selectedLead.id}/tag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tag: newTag.trim() }),
      });
      setNewTag('');
      // Refresh tags (simulate by refetching bio)
      setBioLoading(true);
      const res = await fetch(
        `${apiUrl}/api/Lead/${clientId}/${selectedLead.id}`,
      );
      const data = await res.json();
      setLeadBio(data);
      setLeadTags(
        Array.isArray(data.tags) ? data.tags.map((t: any) => t.name || t) : [],
      );
    } catch {
      message.error('Failed to add tag');
    } finally {
      setTagAdding(false);
      setTagsLoading(false);
      setBioLoading(false);
    }
  };

  return (
    <div style={{ width: '100%', minHeight: '100vh' }}>
      {clientConfig && (
        <ClientNavigation
          activeTab="leads"
          agent={activeAgent}
          agentScoped
          clientId={clientConfig.client_id?.toString()}
          clientName={clientConfig.friendly_name}
          crmLink={clientConfig.referral_url}
          crmType={clientConfig.crm_type}
          isSingleAgent={clientConfig.is_single_agent}
          numericClientId={parseInt(clientId || '0')}
        />
      )}
      <Row gutter={0} style={{ width: '100%' }}>
        <Col
          sm={5}
          style={{
            borderRight: '1px solid #e5e5e5',
            background: '#fff',
            minHeight: '100vh',
            padding: 0,
          }}
          xs={24}
        >
          <div
            style={{
              padding: 0,
              background: '#fff',
              borderBottom: '1px solid #e5e5e5',
            }}
          >
            <Input.Search
              allowClear
              enterButton="Search"
              onChange={e => setLeadSearch(e.target.value)}
              placeholder="Search by lead name, email, or phone"
              style={{
                borderRadius: 0,
                border: 'none',
                boxShadow: 'none',
                padding: 0,
              }}
              value={leadSearch}
            />
          </div>
          <div
            onScroll={handleSidebarScroll}
            style={{ flex: 1, overflow: 'auto', padding: 0, height: '100vh' }}
          >
            <AntList
              dataSource={leads}
              loading={loading}
              renderItem={item => (
                <AntList.Item
                  data-lead-id={item.id}
                  onClick={() => setSelectedLead(item)}
                  onMouseEnter={e =>
                    (e.currentTarget.style.background =
                      item.id === selectedLead?.id ? '#e6f7ff' : '#f5f5f5')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.background =
                      item.id === selectedLead?.id ? '#e6f7ff' : '#fff')
                  }
                  style={{
                    cursor: 'pointer',
                    background:
                      item.id === selectedLead?.id ? '#e6f7ff' : '#fff',
                    borderLeft:
                      item.id === selectedLead?.id
                        ? '4px solid #1890ff'
                        : '4px solid transparent',
                    padding: '10px 16px',
                    margin: 0,
                    fontSize: 15,
                    fontWeight: 500,
                    transition: 'background 0.2s',
                  }}
                >
                  {item.first_name} {item.last_name}
                </AntList.Item>
              )}
              style={{ border: 'none', background: 'transparent', padding: 0 }}
            />
            <div style={{ textAlign: 'center', padding: 8 }}>
              {sidebarHasMore && !loading && (
                <button
                  onClick={() =>
                    fetchSidebarLeads(sidebarPageRef.current + 1, true)
                  }
                >
                  Load More
                </button>
              )}
            </div>
          </div>
          <div style={{ padding: 16, textAlign: 'center' }}>
            {loading && (
              <div style={{ color: '#888', marginTop: 8 }}>
                Loading more leads...
              </div>
            )}
            {!sidebarHasMore && !loading && leads.length === 0 && (
              <div style={{ color: '#888', marginTop: 8 }}>No leads found.</div>
            )}
          </div>
          {error && <CustomAlert message={error} type="error" />}
        </Col>
        <Col
          sm={19}
          style={{ background: '#fff', minHeight: '100vh', padding: 0 }}
          xs={24}
        >
          {selectedLead && (
            <div
              style={{
                padding: 32,
                minWidth: 0,
                maxWidth: 900,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <Tabs defaultActiveKey="info" tabBarGutter={32}>
                <TabPane
                  key="info"
                  tab={
                    <span>
                      <InfoCircleOutlined /> Info
                    </span>
                  }
                >
                  <Card
                    bordered
                    style={{
                      marginBottom: 24,
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Title level={3} style={{ marginBottom: 8 }}>
                      {selectedLead.first_name} {selectedLead.last_name}
                    </Title>
                    <Descriptions bordered column={1} size="middle">
                      <Descriptions.Item label="Email">
                        {selectedLead.email || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Phone">
                        {selectedLead.phone || '-'}
                      </Descriptions.Item>
                      <Descriptions.Item label="Stage">
                        {selectedLead.stage || '-'}
                      </Descriptions.Item>
                      {bioLoading ? (
                        <Descriptions.Item label="Bio" span={2}>
                          <Spin />
                        </Descriptions.Item>
                      ) : leadBio && leadBio.bio ? (
                        <Descriptions.Item label="Bio" span={2}>
                          {leadBio.bio.summary || JSON.stringify(leadBio.bio)}
                        </Descriptions.Item>
                      ) : null}
                      {leadBio && leadBio.user_profile && (
                        <Descriptions.Item label="Profile" span={2}>
                          {leadBio.user_profile.profile_summary || '-'}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                    <div style={{ marginTop: 16 }}>
                      <Text strong>Tags: </Text>
                      {tagsLoading ? (
                        <Spin size="small" />
                      ) : leadTags.length === 0 ? (
                        <Text type="secondary">No tags</Text>
                      ) : (
                        leadTags.map(tag => <AntTag key={tag}>{tag}</AntTag>)
                      )}
                      <Input
                        disabled={tagAdding}
                        onChange={e => setNewTag(e.target.value)}
                        onPressEnter={handleAddTag}
                        placeholder="Add tag"
                        size="small"
                        style={{ width: 120, marginLeft: 8 }}
                        value={newTag}
                      />
                      <Button
                        loading={tagAdding}
                        onClick={handleAddTag}
                        size="small"
                        style={{ marginLeft: 4 }}
                        type="primary"
                      >
                        Add
                      </Button>
                    </div>
                  </Card>
                </TabPane>
                <TabPane
                  key="notes"
                  tab={
                    <span>
                      <FileTextOutlined /> Notes
                    </span>
                  }
                >
                  <Card
                    bordered
                    style={{
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Button
                      onClick={() => setIsCreateNoteVisible(true)}
                      style={{ marginBottom: 12 }}
                      type="primary"
                    >
                      Add Note
                    </Button>
                    {isCreateNoteVisible && (
                      <div
                        style={{
                          marginBottom: 16,
                          background: '#f6f6f6',
                          padding: 16,
                          borderRadius: 8,
                        }}
                      >
                        <Input
                          onChange={e => setNoteHeader(e.target.value)}
                          placeholder="Header (optional)"
                          style={{ marginBottom: 8 }}
                          value={noteHeader}
                        />
                        <Input.TextArea
                          onChange={e => setNoteBody(e.target.value)}
                          placeholder="Note body"
                          rows={4}
                          style={{ marginBottom: 8 }}
                          value={noteBody}
                        />
                        <Button
                          loading={noteLoading}
                          onClick={handleCreateNote}
                          style={{ marginRight: 8 }}
                          type="primary"
                        >
                          Submit Note
                        </Button>
                        <Button
                          disabled={noteLoading}
                          onClick={() => setIsCreateNoteVisible(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                    {notesLoading ? (
                      <Spin />
                    ) : (
                      <AntList
                        dataSource={leadNotes}
                        locale={{
                          emptyText: (
                            <Text type="secondary">No notes found.</Text>
                          ),
                        }}
                        renderItem={note => (
                          <AntList.Item
                            style={{
                              background: '#fafafa',
                              borderRadius: 6,
                              marginBottom: 8,
                            }}
                          >
                            <AntList.Item.Meta
                              description={
                                isHTML(note.body) ? (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeHTML(note.body),
                                    }}
                                  />
                                ) : (
                                  <div>{note.body}</div>
                                )
                              }
                              title={
                                <span style={{ fontWeight: 500 }}>
                                  {note.header || '(No Header)'}
                                </span>
                              }
                            />
                            <div style={{ fontSize: 12, color: '#888' }}>
                              Created:{' '}
                              {note.created_at
                                ? new Date(note.created_at).toLocaleString()
                                : '-'}
                            </div>
                          </AntList.Item>
                        )}
                      />
                    )}
                  </Card>
                </TabPane>
                <TabPane
                  key="tasks"
                  tab={
                    <span>
                      <CheckCircleOutlined /> Tasks
                    </span>
                  }
                >
                  <Card
                    bordered
                    style={{
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Button
                      onClick={() => setIsCreateTaskVisible(true)}
                      style={{ marginBottom: 12 }}
                      type="primary"
                    >
                      Add Task
                    </Button>
                    {isCreateTaskVisible && (
                      <div
                        style={{
                          marginBottom: 16,
                          background: '#f6f6f6',
                          padding: 16,
                          borderRadius: 8,
                        }}
                      >
                        <Input
                          onChange={e => setTaskType(e.target.value)}
                          placeholder="Task Type (e.g. Follow Up)"
                          style={{ marginBottom: 8 }}
                          value={taskType}
                        />
                        <Input
                          onChange={e => setTaskDueDate(e.target.value)}
                          placeholder="Due Date (YYYY-MM-DD)"
                          style={{ marginBottom: 8 }}
                          value={taskDueDate}
                        />
                        <Input.TextArea
                          onChange={e => setTaskDetails(e.target.value)}
                          placeholder="Task details"
                          rows={3}
                          style={{ marginBottom: 8 }}
                          value={taskDetails}
                        />
                        <Button
                          loading={tasksLoading}
                          onClick={handleCreateTask}
                          style={{ marginRight: 8 }}
                          type="primary"
                        >
                          Submit Task
                        </Button>
                        <Button
                          disabled={tasksLoading}
                          onClick={() => setIsCreateTaskVisible(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}
                    {tasksLoading ? (
                      <Spin />
                    ) : (
                      <AntList
                        dataSource={leadTasks}
                        locale={{
                          emptyText: (
                            <Text type="secondary">No tasks found.</Text>
                          ),
                        }}
                        renderItem={task => (
                          <AntList.Item
                            style={{
                              background: '#fafafa',
                              borderRadius: 6,
                              marginBottom: 8,
                            }}
                          >
                            <AntList.Item.Meta
                              description={<span>{task.details}</span>}
                              title={
                                <span style={{ fontWeight: 500 }}>
                                  {task.task_type || '(No Type)'}
                                </span>
                              }
                            />
                            <div style={{ fontSize: 12, color: '#888' }}>
                              Due:{' '}
                              {task.due_date
                                ? new Date(task.due_date).toLocaleDateString()
                                : '-'}
                            </div>
                          </AntList.Item>
                        )}
                      />
                    )}
                  </Card>
                </TabPane>
                <TabPane
                  key="activity"
                  tab={
                    <span>
                      <ThunderboltOutlined /> Activity
                    </span>
                  }
                >
                  <Card
                    bordered
                    style={{
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                  >
                    {activityLoading ? (
                      <Spin />
                    ) : leadActivity.length === 0 ? (
                      <Text type="secondary">No activity found.</Text>
                    ) : (
                      <AntList
                        dataSource={leadActivity}
                        renderItem={activity => (
                          <AntList.Item
                            style={{
                              background: '#fafafa',
                              borderRadius: 6,
                              marginBottom: 8,
                            }}
                          >
                            <AntList.Item.Meta
                              description={
                                <span>{JSON.stringify(activity)}</span>
                              }
                              title={
                                <span style={{ fontWeight: 500 }}>
                                  Activity
                                </span>
                              }
                            />
                          </AntList.Item>
                        )}
                      />
                    )}
                  </Card>
                </TabPane>
                <TabPane
                  key="tags"
                  tab={
                    <span>
                      <TagsOutlined /> Tags
                    </span>
                  }
                >
                  <Card
                    bordered
                    style={{
                      borderRadius: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                  >
                    <div style={{ marginBottom: 16 }}>
                      <Text strong>Tags: </Text>
                      {tagsLoading ? (
                        <Spin size="small" />
                      ) : leadTags.length === 0 ? (
                        <Text type="secondary">No tags</Text>
                      ) : (
                        leadTags.map(tag => <AntTag key={tag}>{tag}</AntTag>)
                      )}
                    </div>
                    <Input
                      disabled={tagAdding}
                      onChange={e => setNewTag(e.target.value)}
                      onPressEnter={handleAddTag}
                      placeholder="Add tag"
                      size="small"
                      style={{ width: 120, marginRight: 8 }}
                      value={newTag}
                    />
                    <Button
                      loading={tagAdding}
                      onClick={handleAddTag}
                      size="small"
                      type="primary"
                    >
                      Add
                    </Button>
                  </Card>
                </TabPane>
              </Tabs>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};
